import { RingBuffer } from "./third_party/ringbufjs/ringbuf.module.js";
import * as THREE from 'three';
import VideoWorker from './pipelines/video.js?worker';
import { bufferManager } from './utils/buffer_manager.js';

const WEBGL_FILTERS = {
    1003: THREE.NearestFilter,
    1006: THREE.LinearFilter,
    1004: THREE.NearestMipmapNearestFilter,
    1007: THREE.LinearMipmapNearestFilter,
    1005: THREE.NearestMipmapLinearFilter,
    1008: THREE.LinearMipmapLinearFilter
};

const WEBGL_WRAPPINGS = {
    1001: THREE.ClampToEdgeWrapping,
    1002: THREE.MirroredRepeatWrapping,
    1000: THREE.RepeatWrapping
};

const GL_COMPONENT_TYPES = {
    5120: Int8Array,
    5121: Uint8Array,
    5122: Int16Array,
    5123: Uint16Array,
    5125: Uint32Array,
    5126: Float32Array
};

/**
 * MPEG_texture_video extension
 * spec: https://github.com/KhronosGroup/glTF/tree/main/extensions/2.0/Vendor/MPEG_texture_video
 *       https://github.com/MPEGGroup/Scene-Description/tree/main/MPEG_texture_video
 *
 * MPEG_texture_video extension provides the possibility to link a texture object
 * defined in glTF 2.0 to media given in the MPEG_media extension
 */
export class GLTFMPEGTextureVideoExtension {
    constructor(parser) {
        this.name = 'MPEG_texture_video';
        this.parser = parser;
        this.updatables = [];
        this.textures = new Map();
        this.sources = new Map();
        this.stereoGroups = new Map(); // Map<groupId, {left: texture, right: texture}>
        this.activeEye = 'left'; // Default to left eye in mono view
        this.loadedTextureIndices = new Set(); // Track loaded texture indices
        this.renderer = parser.options.renderer;

        // Set up view switching
        if (typeof window !== 'undefined') {
            window.setActiveEye = (eye) => {
                if (this.renderer?.xr?.isPresenting) {
                    console.log('Ignoring eye switch in XR mode');
                    return;
                }

                console.log('Switching to', eye, 'eye view');
                this.activeEye = eye;

                // Update UI
                const leftBtn = document.getElementById('leftEyeBtn');
                const rightBtn = document.getElementById('rightEyeBtn');
                if (leftBtn && rightBtn) {
                    leftBtn.classList.toggle('active', eye === 'left');
                    rightBtn.classList.toggle('active', eye === 'right');
                }

                // Update all stereo textures
                this.stereoGroups.forEach((group, groupId) => {
                    if (group.left && group.right) {
                        const activeTexture = eye === 'left' ? group.left : group.right;
                        const inactiveTexture = eye === 'left' ? group.right : group.left;

                        // Update visibility but keep both textures decoding
                        activeTexture.visible = true;
                        inactiveTexture.visible = false;

                        console.log('Updated stereo group:', {
                            groupId,
                            activeEye: eye,
                            leftVisible: group.left.visible,
                            rightVisible: group.right.visible,
                            leftDecoding: group.left.keepDecoding,
                            rightDecoding: group.right.keepDecoding
                        });
                    }
                });
            };
        }
    }

    async loadTexturesInGroup(textureIndex) {
        const parser = this.parser;
        const json = parser.json;
        const textureDef = json.textures[textureIndex];
        const extensionDef = textureDef.extensions?.[this.name];

        if (!extensionDef || !extensionDef.extras?.group_id) {
            return;
        }

        const groupId = extensionDef.extras.group_id;

        // Find all textures in the same group that haven't been loaded yet
        const groupTextures = [];
        json.textures.forEach((tex, idx) => {
            const ext = tex.extensions?.[this.name];
            if (ext?.extras?.group_id === groupId &&
                idx !== textureIndex &&
                !this.loadedTextureIndices.has(idx)) {
                groupTextures.push(idx);
            }
        });

        // Load all textures in the group in parallel
        const promises = groupTextures.map(idx => {            
            var tex = this.loadTexture(idx);
            this.loadedTextureIndices.add(idx); // Mark as being loaded
            return tex;
        });

        await Promise.all(promises);
    }

    async loadTexture(textureIndex) {
        // If this texture has already been loaded, return null
        if (this.loadedTextureIndices.has(textureIndex)) {
            return null;
        }

        console.log('Loading texture:', textureIndex);
        this.loadedTextureIndices.add(textureIndex);

        const parser = this.parser;
        const json = parser.json;
        const textureDef = json.textures[textureIndex];

        if (!textureDef.extensions || !textureDef.extensions[this.name]) {
            console.warn('No MPEG_texture_video extension found for texture:', textureIndex);
            return null;
        }

        // First load this texture
        const texture = await this._loadTextureImpl(textureIndex);

        // Then load other textures in the same group
        await this.loadTexturesInGroup(textureIndex);

        return texture;
    }

    async _loadTextureImpl(textureIndex) {
        const parser = this.parser;
        const json = parser.json;
        const textureDef = json.textures[textureIndex];
        const extensionDef = textureDef.extensions[this.name];
        const samplersDef = json.samplers || [];
        const samplerDef = samplersDef[textureDef.sampler] || {};

        return parser.getDependency('accessor', extensionDef.accessor).then(accessor => {
            return new Promise(async resolve => {
                // Calculate buffer size for RGBA format (4 bytes per pixel)
                const pixelCount = extensionDef.width * extensionDef.height;
                const bytesPerPixel = 4; // RGBA
                const bufferSize = pixelCount * bytesPerPixel;

                // Create unique buffer ID including stereo metadata
                const stereoMask = extensionDef.extras?.mask || 'mono';
                const bufferId = `video-${textureIndex}-${stereoMask}`;
                const maxFrames = 5;

                // Create or get the buffer
                let circularBuffer = bufferManager.getBuffer(bufferId);
                if (!circularBuffer) {
                    console.log(`Creating new buffer ${bufferId}`);
                    try {
                        circularBuffer = bufferManager.createBuffer(
                            bufferId,
                            extensionDef.width,
                            extensionDef.height,
                            4, // RGBA
                            maxFrames,
                            Uint8Array,
                            extensionDef.extras?.group_id
                        );
                    } catch (error) {
                        console.error(`Failed to create buffer ${bufferId}:`, error);
                        throw error;
                    }
                }

                // Create initial texture buffer
                const textureBuffer = new Uint8Array(bufferSize);

                // Create texture with RGBA format
                const texture = new THREE.DataTexture(
                    textureBuffer,
                    extensionDef.width,
                    extensionDef.height,
                    THREE.RGBAFormat,
                    THREE.UnsignedByteType,
                    THREE.UVMapping,
                    THREE.ClampToEdgeWrapping,
                    THREE.ClampToEdgeWrapping,
                    THREE.LinearFilter,
                    THREE.LinearFilter,
                    1,
                    THREE.SRGBColorSpace
                );

                // Store renderer reference
                texture.renderer = this.renderer;

                // Configure texture properties
                texture.format = THREE.RGBAFormat;
                texture.type = THREE.UnsignedByteType;
                texture.colorSpace = THREE.SRGBColorSpace;
                texture.generateMipmaps = false;
                texture.needsUpdate = true;

                // Store MPEG texture metadata
                texture.userData = {
                    sourceId: textureIndex,
                    mediaIndex: 0,
                    width: extensionDef.width,
                    height: extensionDef.height,
                    format: extensionDef.format,
                    textureKey: `video-${textureIndex}-${extensionDef.extras?.mask || 'mono'}`,
                    mpegTextureInfo: {
                        width: extensionDef.width,
                        height: extensionDef.height,
                        format: extensionDef.format,
                        accessor: extensionDef.accessor,
                        frameSize: extensionDef.width * extensionDef.height * 4
                    }
                };

                // Add stereo metadata if present
                if (extensionDef.extras?.group_id !== undefined) {
                    texture.userData.groupId = extensionDef.extras.group_id;
                    texture.userData.groupType = extensionDef.extras.group_type;
                    texture.userData.mask = extensionDef.extras.mask;
                    texture.userData.stereoInfo = {
                        isRightEye: extensionDef.extras.mask === 'right',
                        isLeftEye: extensionDef.extras.mask === 'left',
                        groupId: extensionDef.extras.group_id,
                        groupType: extensionDef.extras.group_type,
                        mask: extensionDef.extras.mask
                    };
                }

                // Store texture in maps
                this.textures.set(texture.userData.textureKey, texture);

                // Handle stereo group management
                if (extensionDef.extras?.group_type === 'stereo' && extensionDef.extras?.group_id) {
                    const groupId = extensionDef.extras.group_id;
                    const isRightEye = extensionDef.extras.mask === 'right';

                    if (!this.stereoGroups.has(groupId)) {
                        this.stereoGroups.set(groupId, {
                            left: null,
                            right: null,
                            groupId: groupId
                        });
                    }

                    const group = this.stereoGroups.get(groupId);
                    if (isRightEye) {
                        group.right = texture;
                    } else {
                        group.left = texture;
                    }

                    // Set visibility based on mode
                    const isXRMode = this.renderer?.xr?.isPresenting ?? false;
                    if (isXRMode) {
                        const xrView = this.renderer.xr.getCamera().layers.mask === 1 ? 'left' : 'right';
                        texture.visible = (isRightEye && xrView === 'right') || (!isRightEye && xrView === 'left');
                    } else {
                        texture.visible = isRightEye ? this.activeEye === 'right' : this.activeEye === 'left';
                    }
                    texture.keepDecoding = true;
                }

                // Add update method
                texture.update = function() {
                    const buffer = bufferManager.getBuffer(this.userData.textureKey);
                    if (!buffer) {
                        console.warn('No buffer found for texture:', this.userData.textureKey);
                        return;
                    }

                    const availableData = buffer.available_read();
                    const frameSize = this.userData.mpegTextureInfo.frameSize;

                    if (availableData >= frameSize) {
                        try {
                            const frameData = new Uint8Array(frameSize);
                            const bytesRead = buffer.pop(frameData);

                            if (bytesRead === frameSize) {
                                this.image.data.set(frameData);

                                const firstPixels = Array.from(frameData.slice(0, 16));
                                const hasData = firstPixels.some(v => v !== 0);
                                if (hasData) {
                                    console.log('Valid frame data received:', {
                                        textureKey: this.userData.textureKey,
                                        eye: this.userData.mask,
                                        firstPixels,
                                        bytesRead,
                                        frameSize
                                    });
                                }

                                this.format = THREE.RGBAFormat;
                                this.type = THREE.UnsignedByteType;
                                this.colorSpace = THREE.SRGBColorSpace;
                                this.generateMipmaps = false;
                                this.needsUpdate = true;

                                if (this.renderer) {
                                    this.renderer.initTexture(this);
                                }
                            }
                        } catch (error) {
                            console.error('[Texture] Error updating:', error);
                        }
                    }
                };

                // Add to updatables list
                this.updatables.push(texture);

                // Create video source
                const videoSource = {
                    sourceId: textureIndex,
                    circularBuffer: circularBuffer,
                    texture: texture,
                    width: extensionDef.width,
                    height: extensionDef.height,
                    format: extensionDef.format,
                    stereoType: extensionDef.extras?.group_type || 'mono',
                    stereoMask: extensionDef.extras?.mask || null,
                    groupId: extensionDef.extras?.group_id || null,
                    keepDecoding: true, // Always decode regardless of visibility
                    textureKey: texture.userData.textureKey
                };

                // Store video source in the sources map
                this.sources.set(texture.userData.textureKey, videoSource);

                console.log('Created video source:', {
                    sourceId: textureIndex,
                    textureKey: texture.userData.textureKey,
                    stereoType: videoSource.stereoType,
                    stereoMask: videoSource.stereoMask,
                    groupId: videoSource.groupId,
                    dimensions: `${extensionDef.width}x${extensionDef.height}`
                });

                // Find the media index from the GLTF data
                // TODO: This is a hacky way to find the media index, should be improved
                let mediaIdx = textureIndex;
                let mediaDef;

                // If we found a media index, get the media definition
                if (mediaIdx !== undefined && parser.plugins.MPEG_media?.metadata?.media) {
                    mediaDef = parser.plugins.MPEG_media.metadata.media[mediaIdx].alternatives[0];
                } else {
                    // Fallback to default media definition
                    mediaDef = {
                        uri: extensionDef.extras?.mask === 'right' ? 'test.right.mp4' : 'test.left.mp4',
                        mimeType: 'video/mp4'
                    };
                }

                // Handle stereo video files
                if (extensionDef.extras?.group_type === 'stereo') {
                    const isRightEye = extensionDef.extras.mask === 'right';
                    const isLeftEye = extensionDef.extras.mask === 'left';

                    if (!isRightEye && !isLeftEye) {
                        console.error('Invalid stereo mask:', extensionDef.extras.mask);
                        throw new Error('Invalid stereo mask');
                    }

                    mediaDef = {
                        ...mediaDef,
                        stereoMask: extensionDef.extras.mask,
                        groupId: extensionDef.extras.group_id,
                        stereoInfo: {
                            isRightEye,
                            isLeftEye,
                            groupType: extensionDef.extras.group_type,
                            groupId: extensionDef.extras.group_id
                        }
                    };

                    console.log('Using stereo video:', {
                        uri: mediaDef.uri,
                        mask: mediaDef.stereoMask,
                        groupId: mediaDef.groupId,
                        isRightEye,
                        isLeftEye
                    });
                }

                // Initialize video worker
                const mediaPath = '../../' + parser.options.path;
                const worker = new VideoWorker();
                texture.worker = worker;

                worker.postMessage({
                    command: 'initialize',
                    sab: circularBuffer.buf,
                    uri: mediaPath + mediaDef.uri,
                    track: 0,
                    type: accessor.properties.type,
                    componentType: accessor.properties.componentType,
                    width: extensionDef.width,
                    height: extensionDef.height,
                    textureIndex: textureIndex,
                    stereoMask: extensionDef.extras?.mask || 'mono',
                    groupId: extensionDef.extras?.group_id || null,
                    options: {
                        bufferId: texture.userData.textureKey,
                        groupId: extensionDef.extras?.group_id || null,
                        autoplay: true
                    }
                });

                resolve(texture);
            });
        });
    }

    afterRoot(result) {
        if (!result.userData.MPEG_media) {
            result.userData.MPEG_media = {};
        }
        if (!result.userData.MPEG_media.updatables) {
            result.userData.MPEG_media.updatables = [];
        }

        result.userData.MPEG_media.updatables = result.userData.MPEG_media.updatables.concat(this.updatables);

        // Set up stereo groups
        const stereoGroups = new Map();
        let leftCount = 0;
        let rightCount = 0;

        result.scene.traverse(node => {
            if (node.material?.map?.update) {
                const texture = node.material.map;
                if (texture.userData.groupId !== undefined && texture.userData.groupType === 'stereo') {
                    if (!stereoGroups.has(texture.userData.groupId)) {
                        stereoGroups.set(texture.userData.groupId, {
                            left: null,
                            right: null,
                            materials: new Set()
                        });
                    }
                    const group = stereoGroups.get(texture.userData.groupId);
                    if (texture.userData.mask === 'left') {
                        group.left = texture;
                        leftCount++;
                    } else if (texture.userData.mask === 'right') {
                        group.right = texture;
                        rightCount++;
                    }
                    group.materials.add(node.material);
                }
            }
        });

        // Set up materials for stereo rendering
        result.scene.traverse(node => {
            if (node.material?.map?.update) {
                const texture = node.material.map;
                if (texture.userData.groupId !== undefined && texture.userData.groupType === 'stereo') {
                    const group = stereoGroups.get(texture.userData.groupId);
                    node.material.userData.originalMap = texture;
                    node.material.userData.stereoGroup = group;

                    node.material.onBeforeRender = function(renderer, scene, camera, geometry, object, group) {
                        if (renderer.xr.isPresenting) {
                            const isLeftEye = camera.layers.mask === 1;
                            if (this.userData.stereoGroup) {
                                this.map = isLeftEye ? this.userData.stereoGroup.left : this.userData.stereoGroup.right;
                            }
                        } else {
                            this.map = this.userData.originalMap;
                        }
                    };
                }
            }
        });

        result.userData.stereoGroups = stereoGroups;
    }
}